import { SSEConfig } from './config';
import { SSEEventProcessor } from './handler';
import EventSourceLib from 'eventsource';
import {
  DEFAULT_MAX_RETRIES,
  EVENT_TYPES,
  INITIAL_RECONNECT_DELAY,
  MAX_RECONNECT_DELAY,
  RECONNECT_JITTER,
} from './const';

export class SSEConnectionManager {
  private eventSource: EventSourceLib | undefined;
  private connectTimeout: NodeJS.Timeout | null = null;
  private retryCount = 0;
  private reconnectDelay = INITIAL_RECONNECT_DELAY;

  constructor(
    private config: SSEConfig,
    private eventHandler: SSEEventProcessor,
    private maxRetries: number = DEFAULT_MAX_RETRIES,
  ) {}

  public connect(): void {
    if (this.eventSource) this.close();

    const sseUrl = this.config.getSSEUrl();
    const headers = this.config.getHeaders();

    this.eventSource = new EventSourceLib(sseUrl, { headers });

    // Не обрабатываем все эвенты, только наши кастомные
    for (const eventType of Object.values(EVENT_TYPES)) {
      this.eventSource.addEventListener(eventType, async (event: MessageEvent) => {
        await this.eventHandler.handleMessage(event.data);
      });
    }
    this.eventSource.onerror = (err: Event) => this.handleError(err);
    this.eventSource.onopen = () => this.handleOpen();

    this.setConnectionTimeout();
  }

  private handleError(err: Event): void {
    console.error('SSE connection error:', err);
    this.close();
    this.retryConnection();
  }

  private handleOpen(): void {
    this.clearConnectionTimeout();
    this.resetReconnectParameters();
  }

  /**
   * Переподключение с увеличенной задержкой в случае ошибок
   */
  private retryConnection(): void {
    if (this.retryCount >= this.maxRetries) {
      console.error('Max reconnection attempts reached. Giving up.');
      return;
    }

    const jitter = Math.random() * RECONNECT_JITTER;
    const reconnectTimeout = Math.min(MAX_RECONNECT_DELAY, this.reconnectDelay * Math.pow(2, this.retryCount)) + jitter;

    this.retryCount++;

    console.warn(`Reconnecting in ${reconnectTimeout} ms... (Attempt ${this.retryCount})`);

    this.connectTimeout = setTimeout(() => this.connect(), reconnectTimeout);
  }

  private setConnectionTimeout(): void {
    this.connectTimeout = setTimeout(() => {
      if (this.eventSource || this.eventSource?.readyState !== EventSourceLib.OPEN) {
        console.warn('Connection attempt timed out or is no longer valid. Retrying...');
        this.close();
        this.retryConnection();
      }
    }, MAX_RECONNECT_DELAY);
  }

  private clearConnectionTimeout(): void {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  private resetReconnectParameters(): void {
    this.reconnectDelay = INITIAL_RECONNECT_DELAY;
    this.retryCount = 0;
  }

  public close(): void {
    this.clearConnectionTimeout();

    if (this.eventSource) {
      // Подчищаем все обработчики
      for (const eventType of Object.values(EVENT_TYPES)) {
        this.eventSource.removeEventListener(eventType, this.eventHandler.handleMessage);
      }

      this.eventSource.onerror = null;
      this.eventSource.onopen = null;

      this.eventSource.close();
      this.eventSource = undefined;
    }
  }
}
